/**
 * 10.9T — ESCOLHA DE CLASSE. Aparece ao entrar no jogo quando o jogador ainda não tem classe.
 * Cada classe modifica os stats base + arma inicial (ver GameConfig.classes). UI pura: o Game
 * aplica a escolha (progress.classId + startRun).
 */
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";

// 10.9AF — sugestões de nome (tema do Void). As já usadas são filtradas na hora de sugerir.
const NAME_POOL = [
  "Kael", "Nyx", "Draven", "Lyra", "Zarek", "Vesper", "Ronan", "Sable",
  "Thorne", "Isolde", "Grimm", "Seraph", "Vex", "Orin", "Mordra", "Ashen",
  "Corvus", "Elara", "Rook", "Nova", "Dusk", "Valen", "Kira", "Onyx",
  "Fenrir", "Astra", "Bane", "Selene", "Riven", "Zephyr",
];

interface CharSummary { classId: string; level: number; name: string }

export class ClassSelectScreen {
  private readonly root: HTMLDivElement;
  private existing: CharSummary[] = [];
  private canClose = false;
  private confirmDelete: string | null = null; // 10.9Y — classe pendente de exclusão
  private naming: string | null = null; // 10.9AF — classe em criação (passo de nome)
  private nameError = "";
  private suggestions: string[] = [];
  private nameDraft = "";

  constructor(
    uiRoot: HTMLElement,
    private readonly onChoose: (classId: string, name?: string) => void, // 10.9AF — nome ao criar
    private readonly onClose?: () => void,
    private readonly onDelete?: (classId: string) => void, // 10.9Y — excluir personagem
  ) {
    injectStyles();
    this.root = document.createElement("div");
    this.root.className = "overlay cls-overlay";
    this.root.hidden = true;
    uiRoot.appendChild(this.root);
  }

  /** `existing` = personagens já criados (classId+nível+nome). `canClose` mostra o ✕ (quando já há personagem). */
  open(existing: CharSummary[] = [], canClose = false): void {
    this.existing = existing;
    this.canClose = canClose;
    this.confirmDelete = null;
    this.naming = null;
    this.nameError = "";
    this.nameDraft = "";
    this.render();
    this.root.hidden = false;
  }

  /** Nomes já usados (case-insensitive) — o novo nome não pode repetir. */
  private usedNames(): Set<string> {
    return new Set(this.existing.map((e) => e.name.trim().toLowerCase()).filter((n) => n.length > 0));
  }
  hide(): void { this.root.hidden = true; }

  private render(): void {
    if (this.naming) { this.renderNameStep(); return; } // 10.9AF — passo de nome ao criar
    const cards = GameConfig.classes.map((c) => {
      const stats = this.statLines(c);
      const ex = this.existing.find((e) => e.classId === c.id);
      const btn = ex
        ? `${ex.name ? esc(ex.name) + " · " : ""}Nv ${ex.level} · ${esc(i18n.t("class.play"))}`
        : esc(i18n.t("class.create"));
      const del = ex && this.onDelete ? `<button class="cls-del" data-del="${c.id}" aria-label="${esc(i18n.t("class.delete"))}">🗑</button>` : "";
      return `
        <div class="cls-card${ex ? " has" : ""}" style="--cc:${c.color}">
          ${del}
          <button class="cls-main" data-id="${c.id}">
            <span class="cls-ic">${c.icon}</span>
            <span class="cls-name" style="color:${c.color}">${esc(i18n.t(c.nameKey))}</span>
            <span class="cls-desc">${esc(i18n.t(c.descKey))}</span>
            <span class="cls-stats">${stats}</span>
            <span class="cls-pick">${btn}</span>
          </button>
        </div>`;
    }).join("");
    const anyExisting = this.existing.length > 0;
    // banner de confirmação de exclusão (destrutivo)
    const delName = this.confirmDelete ? i18n.t(`class.${this.confirmDelete}.name`) : "";
    const confirmBar = this.confirmDelete ? `
      <div class="cls-confirm">
        <div class="cls-confirm-txt">${esc(i18n.t("class.deleteConfirm", { name: delName }))}</div>
        <div class="cls-confirm-btns">
          <button class="btn danger" data-a="del-yes">${esc(i18n.t("class.delete"))}</button>
          <button class="btn" data-a="del-no">${esc(i18n.t("menu.back"))}</button>
        </div>
      </div>` : "";
    this.root.innerHTML = `
      <div class="panel cls-panel">
        ${this.canClose ? `<button class="btn cls-x" data-a="close" aria-label="${esc(i18n.t("common.close"))}">✕</button>` : ""}
        <h2 class="cls-title">${esc(i18n.t(anyExisting ? "class.charsTitle" : "class.title"))}</h2>
        <div class="cls-sub">${esc(i18n.t("class.sub"))}</div>
        ${confirmBar}
        <div class="cls-list">${cards}</div>
      </div>`;
    this.root.querySelectorAll<HTMLButtonElement>(".cls-main").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.id;
        if (!id) return;
        // existente → JOGAR direto; novo → passo de NOME
        if (this.existing.some((e) => e.classId === id)) this.onChoose(id);
        else this.startNaming(id);
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>(".cls-del").forEach((btn) => {
      btn.addEventListener("click", () => { this.confirmDelete = btn.dataset.del ?? null; this.render(); });
    });
    this.root.querySelector('[data-a="del-yes"]')?.addEventListener("click", () => {
      const id = this.confirmDelete; this.confirmDelete = null;
      if (id) this.onDelete?.(id);
    });
    this.root.querySelector('[data-a="del-no"]')?.addEventListener("click", () => { this.confirmDelete = null; this.render(); });
    this.root.querySelector('[data-a="close"]')?.addEventListener("click", () => this.onClose?.());
  }

  /** 10.9AF — entra no passo de nome de uma NOVA classe: sorteia sugestões livres (não repetidas). */
  private startNaming(classId: string): void {
    this.naming = classId;
    this.nameDraft = "";
    this.nameError = "";
    const used = this.usedNames();
    this.suggestions = NAME_POOL.filter((n) => !used.has(n.toLowerCase()))
      .sort(() => Math.random() - 0.5).slice(0, 5);
    this.render();
  }

  /** Passo de nome: input + sugestões + validação de nome ÚNICO. */
  private renderNameStep(): void {
    const cls = GameConfig.classes.find((c) => c.id === this.naming);
    if (!cls) { this.naming = null; this.render(); return; }
    const chips = this.suggestions.map((n) => `<button class="cls-sugg" data-name="${esc(n)}">${esc(n)}</button>`).join("");
    this.root.innerHTML = `
      <div class="panel cls-panel">
        <h2 class="cls-title">${esc(i18n.t("class.nameTitle", { cls: i18n.t(cls.nameKey) }))}</h2>
        <div class="cls-sub">${esc(i18n.t("class.nameSub"))}</div>
        <div class="cls-name-wrap" style="--cc:${cls.color}">
          <span class="cls-name-ic">${cls.icon}</span>
          <input class="cls-name-input" type="text" maxlength="14" autocomplete="off" spellcheck="false"
            aria-label="${esc(i18n.t("class.namePlaceholder"))}" aria-invalid="${Boolean(this.nameError)}"
            aria-describedby="cls-name-error" placeholder="${esc(i18n.t("class.namePlaceholder"))}" value="${esc(this.nameDraft)}" />
        </div>
        <div class="cls-name-err" id="cls-name-error" role="status">${esc(this.nameError)}</div>
        <div class="cls-sugg-row">${chips}</div>
        <div class="cls-confirm-btns">
          <button class="btn primary" data-a="name-ok">${esc(i18n.t("class.nameConfirm"))}</button>
          <button class="btn" data-a="name-back">${esc(i18n.t("menu.back"))}</button>
        </div>
      </div>`;
    const input = this.root.querySelector<HTMLInputElement>(".cls-name-input")!;
    window.setTimeout(() => input.focus(), 60);
    this.root.querySelectorAll<HTMLButtonElement>(".cls-sugg").forEach((b) => {
      b.addEventListener("click", () => { input.value = b.dataset.name ?? ""; input.focus(); });
    });
    const submit = (): void => {
      const raw = input.value.trim().replace(/\s+/g, " ");
      this.nameDraft = input.value;
      if (raw.length < 2) { this.nameError = i18n.t("class.nameTooShort"); this.render(); return; }
      if (this.usedNames().has(raw.toLowerCase())) { this.nameError = i18n.t("class.nameTaken"); this.render(); return; }
      const id = this.naming!;
      this.naming = null;
      this.onChoose(id, raw);
    };
    this.root.querySelector('[data-a="name-ok"]')?.addEventListener("click", submit);
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") submit(); });
    this.root.querySelector('[data-a="name-back"]')?.addEventListener("click", () => { this.naming = null; this.nameError = ""; this.render(); });
  }

  /** Destaques de stat (só o que foge do base) como chips +/−. */
  private statLines(c: typeof GameConfig.classes[number]): string {
    const chips: string[] = [];
    const pct = (m: number): string => `${m >= 0 ? "+" : ""}${Math.round(m * 100)}%`;
    const hp = c.hpMult as number, dmg = c.damageMult as number, aspd = c.attackSpeedMult as number;
    const move = c.moveSpeedMult as number, crit = c.critChance as number, armor = c.armorFlat as number;
    if (hp !== 1) chips.push(chip(`❤️ ${pct(hp - 1)}`, hp > 1));
    if (dmg !== 1) chips.push(chip(`⚔️ ${pct(dmg - 1)}`, dmg > 1));
    if (aspd !== 1) chips.push(chip(`⚡ ${pct(aspd - 1)}`, aspd > 1));
    if (crit > 0) chips.push(chip(`🎯 +${Math.round(crit * 100)}%`, true));
    if (armor > 0) chips.push(chip(`🛡️ +${armor}`, true));
    if (move !== 1) chips.push(chip(`👟 ${pct(move - 1)}`, move > 1));
    return chips.join("");
  }
}

function chip(txt: string, good: boolean): string {
  return `<span class="cls-chip ${good ? "up" : "down"}">${esc(txt)}</span>`;
}
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  stylesInjected = true;
  const css = `
  .cls-panel{position:relative;max-width:460px;width:94%;max-height:90vh;display:flex;flex-direction:column;gap:8px;overflow-y:auto}
  .cls-x{position:absolute;top:10px;right:10px;width:40px;min-width:0;height:40px;padding:0;font-weight:800;z-index:2}
  .cls-card.has{border-width:2px}
  .cls-title{margin:0;font-size:22px;color:#eaf1ff;text-align:center}
  .cls-sub{font-size:12px;color:#9aa7d6;text-align:center;margin-bottom:6px}
  .cls-list{display:flex;flex-direction:column;gap:10px}
  .cls-card{position:relative;border-radius:14px;border:1px solid color-mix(in srgb, var(--cc) 55%, transparent);
    background:linear-gradient(160deg, color-mix(in srgb, var(--cc) 16%, transparent), rgba(8,10,22,.5))}
  .cls-main{width:100%;display:flex;flex-direction:column;align-items:center;gap:5px;text-align:center;cursor:pointer;
    color:inherit;background:transparent;border:none;padding:14px 12px;border-radius:14px}
  .cls-main:active{transform:scale(0.98)}
  .cls-del{position:absolute;top:8px;right:8px;width:36px;height:36px;padding:0;font-size:16px;border-radius:9px;z-index:2;
    background:rgba(220,60,60,.16);border:1px solid rgba(255,90,90,.5);color:#ff9a9a;cursor:pointer}
  .cls-confirm{background:rgba(40,12,16,.7);border:1px solid rgba(229,72,77,.55);border-radius:10px;padding:10px 12px;display:flex;flex-direction:column;gap:8px}
  .cls-confirm-txt{font-size:13px;color:#ffb3b5;text-align:center;line-height:1.35}
  .cls-confirm-btns{display:flex;gap:8px}
  .cls-confirm-btns .btn{flex:1}
  .cls-ic{font-size:36px;line-height:1}
  .cls-name{font-weight:900;font-size:18px}
  .cls-desc{font-size:12px;color:#c9d4f0;line-height:1.35;max-width:340px}
  .cls-stats{display:flex;flex-wrap:wrap;gap:5px;justify-content:center;margin-top:2px}
  .cls-chip{font-size:11px;font-weight:800;padding:2px 8px;border-radius:999px;background:rgba(255,255,255,.06)}
  .cls-chip.up{color:#83e17f}
  .cls-chip.down{color:#ff9a5a}
  .cls-pick{margin-top:6px;font-weight:900;font-size:14px;color:#0a1428;padding:8px 22px;border-radius:10px;
    background:linear-gradient(135deg, color-mix(in srgb, var(--cc) 85%, white), var(--cc))}
  /* 10.9AF — passo de NOME do personagem */
  .cls-name-wrap{display:flex;align-items:center;gap:10px;margin:6px 0;padding:6px 12px;border-radius:12px;
    border:1px solid color-mix(in srgb, var(--cc) 55%, transparent);background:rgba(8,10,22,.45)}
  .cls-name-ic{font-size:30px;line-height:1}
  .cls-name-input{flex:1;background:transparent;border:none;outline:none;color:#eaf1ff;font-size:20px;font-weight:800;
    padding:10px 4px;letter-spacing:.3px}
  .cls-name-input::placeholder{color:#67709a;font-weight:600}
  .cls-name-err{color:#ff9a9a;font-size:13px;text-align:center;font-weight:700}
  .cls-sugg-row{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;margin:4px 0 8px}
  .cls-sugg{font-size:13px;font-weight:800;padding:7px 14px;border-radius:999px;cursor:pointer;
    background:rgba(255,255,255,.07);border:1px solid rgba(160,140,255,.35);color:#cdd6f5}
  .cls-sugg:active{transform:scale(.96)}`;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
}
